import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminPaginatedEnvelope,
  AfriexCurrency,
  AfriexCurrencyCode,
  AfriexQueryParams,
  AfriexTransactionProcessor,
  AfriexTransactionType,
} from '@/types';

const commonEndpoint = '/v2/admin/currencies';

export type CurrencyResponse = AdminPaginatedEnvelope<AfriexCurrency[]>;
export type CurrencyFilters = AfriexQueryParams;

export type UpdateProcessorRequest = {
  currency: AfriexCurrencyCode;
  type: AfriexTransactionType;
  paymentMethodName: AfriexTransactionProcessor;
};

export const currencyService = {
  getList: async (filters: CurrencyFilters): Promise<CurrencyResponse> => {
    const params = {
      page: 0,
      limit: 20,
      // `params`, `user`, `business` and `permissions` are requested bare
      // rather than as dot paths: the `columns` query parameter becomes a
      // Mongo projection and matches top-level names, and the response
      // whitelist then narrows each subtree (fees out of `params`, the five
      // corridor kill switches out of `permissions`). They feed the
      // Settings -> Currencies expanded row.
      //
      // The two Processors screens pass their own `columns` and are
      // unaffected; this default is read only by `useCurrencyUpdate`.
      columns: [
        'name',
        'code',
        'symbol',
        'isActive',
        'params',
        'user',
        'business',
        'permissions',
      ],
      ...filters,
    };
    const url = `${commonEndpoint}?${toQueryString(params)}`;
    const response = await axios.get<CurrencyResponse>(url);
    return response.data;
  },

  getItem: async (currencyCode: string): Promise<AfriexCurrency> => {
    const response = await axios.get<AfriexCurrency>(
      commonEndpoint + `/${currencyCode}`,
    );

    return response.data;
  },
  saveOne: async (
    itemId: string,
    body: Partial<AfriexCurrency>,
  ): Promise<AfriexCurrency> => {
    const response = await axios.patch<AfriexCurrency>(
      commonEndpoint + `/${itemId}`,
      body,
    );
    return response.data;
  },
};
